import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Card } from 'tea-component';
import Chart from '@/pages/dashboard/components/Chart';
import Abnormal from '@home/components/Abnormal';

import { useStudioCardHorBar } from './_hooks';
import type { IStudioCardHorBarProps } from './_type';
import './index.scss';
import RateSlot from '../RateSlot';
import StudioMetricValueText from './StudioMetricValueText';

export type {
  IHeaderHintByDateType,
  IHeaderHintLabelByDateType,
  IStudioCardHorBarProps,
  IYAxisDateFormatByDateType,
} from './_type';

export default function StudioCardHorBar(props: IStudioCardHorBarProps) {
  const {
    className, style, children, right,
    chartHeight,
    showHeader = true,
    showDod = true,
    preFix = '', endFix = '',
  } = props;

  const {
    displayTitle, displaySubtitle,
    displayHeaderHint,
    cardValue, rate, formatRule,
    chartLoading, errorMsg, renderList,
  } = useStudioCardHorBar(props);

  const dynamicHeight = renderList?.dataSource?.length
    ? Math.max(renderList.dataSource.length * 40 + 64, 220)
    : 220;
  const mergedChartHeight = chartHeight || dynamicHeight;
  const chartStyle = useMemo(() => ({ height: `${mergedChartHeight}px` }), [mergedChartHeight]);

  const header = showHeader ? (
    <div className="dbh-studio-card-hor-bar__metric-header">
      <div className="dbh-studio-card-hor-bar__value-row">
        <StudioMetricValueText
          className="dbh-studio-card-hor-bar__value"
          value={cardValue}
          formatRule={formatRule}
          preFix={preFix}
          endFix={endFix}
        />
        {(() => {
          const hasRate = rate !== '' && rate !== undefined && rate !== null;
          if (showDod && hasRate) {
            return (
              <RateSlot
                className="dbh-studio-card-hor-bar__rate"
                rate={rate}
                hasPlaceholder={false}
                permission
              />
            );
          }
          if (!showDod && displayHeaderHint) {
            return (
              <span className="dbh-studio-card-hor-bar__hint-inline">
                {displayHeaderHint}
              </span>
            );
          }
          return null;
        })()}
      </div>
      {displaySubtitle && (
        <div className="dbh-studio-card-hor-bar__subtitle">{displaySubtitle}</div>
      )}
    </div>
  ) : null;

  const isNoData = Boolean(renderList?.nodata);

  const titleNode = (
    <div className="dbh-studio-card-hor-bar__title-row">
      <span className="dbh-studio-card-hor-bar__title-main">{displayTitle}</span>
    </div>
  );

  return (
    <Card
      className={classNames(
        'dbh-studio-card-hor-bar',
        'studio-card-hor-bar-card',
        className,
      )}
      style={style}
    >
      <Card.Body
        title={titleNode}
        subtitle={null}
        operation={right}
      >
        {header}
        <Abnormal
          isLoading={chartLoading}
          isError={Boolean(errorMsg)}
          isNoData={isNoData}
          style={chartStyle}
          classNames="dbh-studio-card-hor-bar__abnormal"
          component={
            <div className="dbh-studio-card-hor-bar__chart-wrap" style={chartStyle}>
              <Chart option={renderList.options} notMerge />
            </div>
          }
        />
        {children}
      </Card.Body>
    </Card>
  );
}
